import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Archive, BriefcaseBusiness, Ellipsis, Pencil, Plus, Smartphone } from 'lucide-react'
import { useState } from 'react'
import { photoSrc } from '@/api/queries'
import { classTypesQuery, providersQuery, ratesQuery, useRetireClassType, type ClassType } from '@/api/services'
import { useCompanyCaller } from '@/app/company'
import { ServicePanel } from '@/app/services/service-panel'
import { ServicesShell, type ServicesContext } from '@/app/services/services-shell'
import { errorText } from '@/domain/errors'
import { formatMoney } from '@/domain/pass-rules'
import { generalRulesFor, payoutSummary, rulesForService, serviceAbilities } from '@/domain/services'
import { ageBandText, categorySuggestions, groupByCategory } from '@/domain/timetable'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/services/catalogue')({
  component: Catalogue,
})

function Catalogue() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const manage = serviceAbilities(caller).manageCatalogue
  const types = useQuery({ ...classTypesQuery(orgId), enabled: manage })
  const [opened, setOpened] = useState<ClassType | 'new' | null>(null)
  // «Нова послуга» — the page's one primary action; on an empty catalogue it
  // is the empty state's instead.
  const actions = manage && (types.data ?? []).length > 0 ? <PrimaryAdd onAdd={() => setOpened('new')} /> : undefined
  return (
    <ServicesShell orgId={orgId} tab="catalogue" actions={actions}>
      {(ctx) => <CatalogueScreen ctx={ctx} opened={opened} setOpened={setOpened} />}
    </ServicesShell>
  )
}

const PrimaryAdd = ({ onAdd }: { onAdd: () => void }) => (
  <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={onAdd}>
    {t.services.catalogue.add}
  </Button>
)

/// «Послуги»: the catalogue as the director reads it — what, how long (and
/// the break after), for how much, who provides it, what they get, and
/// whether the app sells it. Retired services sit apart, greyed.
function CatalogueScreen({
  ctx,
  opened,
  setOpened,
}: {
  ctx: ServicesContext
  opened: ClassType | 'new' | null
  setOpened: (next: ClassType | 'new' | null) => void
}) {
  const { orgId, can } = ctx
  const c = t.services.catalogue
  const types = useQuery(classTypesQuery(orgId))
  const rates = useQuery({ ...ratesQuery(orgId), enabled: can.seePayout })
  const all = types.data ?? []
  const personal = all.filter((x) => x.kind === 'personal')
  const providerLists = useQueries({ queries: personal.map((x) => providersQuery(orgId, x.id)) })
  const providerIds = new Map(
    personal.map((x, index) => [
      x.id,
      (providerLists[index]?.data ?? []).filter((p) => p.status === 'active').map((p) => p.userId),
    ]),
  )
  const providerCount = new Map([...providerIds].map(([id, ids]) => [id, ids.length]))
  const [retiring, setRetiring] = useState<ClassType | null>(null)
  const [news, setNews] = useState<string | null>(null)
  const retire = useRetireClassType(orgId)

  const active = all.filter((x) => x.status === 'active')
  const retired = all.filter((x) => x.status !== 'active')
  const open = typeof opened === 'object' && opened ? (all.find((x) => x.id === opened.id) ?? opened) : opened

  const columns: readonly DataColumn<ClassType>[] = [
    {
      key: 'name',
      header: c.columns.name,
      width: 'minmax(0,1.6fr)',
      cell: (x) => <ServiceName service={x} />,
    },
    {
      key: 'ages',
      header: c.columns.ages,
      width: 'minmax(88px,0.6fr)',
      hideBelow: 'lg',
      cell: (x) => {
        const band = ageBandText(x)
        return band ? <span className="tabular">{band}</span> : <span className="text-muted">—</span>
      },
    },
    {
      key: 'kind',
      header: t.services.service.kind,
      width: 'minmax(0,0.8fr)',
      hideBelow: 'lg',
      cell: (x) => t.services.kinds[x.kind],
    },
    {
      key: 'duration',
      header: c.columns.duration,
      width: 'minmax(96px,0.8fr)',
      hideBelow: 'md',
      cell: (x) => <span className="tabular">{c.durationWithBuffer(x.durationMinutes, x.kind === 'personal' ? x.bufferMinutes : 0)}</span>,
    },
    {
      key: 'providers',
      header: c.columns.providers,
      width: 'minmax(0,0.7fr)',
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (x) =>
        x.kind === 'group' ? (
          <span className="text-muted">—</span>
        ) : providerCount.get(x.id) || x.status !== 'active' ? (
          t.services.providers(providerCount.get(x.id) ?? 0)
        ) : (
          <span className="text-warning">{c.noProviders}</span>
        ),
    },
    ...(can.seePayout
      ? [
          {
            key: 'payout',
            header: c.columns.payout,
            width: 'minmax(0,1.2fr)',
            hideBelow: 'md',
            mobile: 'hidden',
            cell: (x: ClassType) => {
              // First names: the column is narrow and the panel has the full ones.
              const words = payoutSummary(rulesForService(rates.data ?? [], x.id), (id) => ctx.nameOf(id).split(' ')[0] ?? '')
              if (words) return <span title={words}>{words}</span>
              // No rule about the service, but a general one still pays its sales.
              const general = generalRulesFor(rates.data ?? [], x.kind, x.kind === 'group' ? null : (providerIds.get(x.id) ?? []))
              return <span className="text-muted">{general.length > 0 ? c.generalPayout : c.noPayout}</span>
            },
          } satisfies DataColumn<ClassType>,
        ]
      : []),
    {
      key: 'online',
      header: c.columns.online,
      width: '120px',
      hideBelow: 'xl',
      mobile: 'hidden',
      cell: (x) =>
        x.bookableOnline ? (
          <span className="inline-flex items-center gap-1.5">
            <Smartphone aria-hidden="true" className="size-3.5" />
            {c.online}
          </span>
        ) : (
          <span className="text-muted">{c.offline}</span>
        ),
    },
    {
      key: 'price',
      header: c.columns.price,
      width: '168px',
      numeric: true,
      mobile: 'trailing',
      cell: (x) =>
        x.priceMinor !== null ? (
          <span className={cn('text-callout', x.status === 'active' && 'font-medium text-text')}>{formatMoney(x.priceMinor)}</span>
        ) : (
          <span className="text-caption text-muted">{c.byPass}</span>
        ),
    },
  ]

  const menu = (x: ClassType) =>
    can.manageCatalogue && x.status === 'active' ? (
      <RowMenu label={`${t.app.more}: ${x.name}`} trigger={<Ellipsis className="size-[18px]" />}>
        <RowMenuItem icon={<Pencil className="size-4" />} onSelect={() => setOpened(x)}>
          {c.edit}
        </RowMenuItem>
        <RowMenuItem destructive icon={<Archive className="size-4" />} onSelect={() => setRetiring(x)}>
          {c.retire}
        </RowMenuItem>
      </RowMenu>
    ) : null

  const table = (rows: readonly ClassType[], label: string) => (
    <DataList
      label={label}
      columns={columns}
      rows={rows}
      rowKey={(x) => x.id}
      rowTarget={(x) => ({ onClick: () => setOpened(x) })}
      rowLabel={(x) => `${can.manageCatalogue ? c.edit : t.passes.plans.open}: ${x.name}`}
      rowActions={can.manageCatalogue ? menu : undefined}
      rowTone={(x) => (x.status === 'active' ? 'normal' : 'muted')}
      empty={c.empty}
    />
  )

  return (
    <>
      {news ? <InlineBanner kind="done" className="mb-6" message={news} /> : null}
      {types.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : types.isError ? (
        <InlineBanner kind="error" message={errorText(types.error)} action={{ label: t.app.retry, onClick: () => void types.refetch() }} />
      ) : all.length === 0 ? (
        <EmptyState
          icon={<BriefcaseBusiness />}
          title={c.empty}
          message={can.manageCatalogue ? c.emptyMessage : c.emptyReadOnly}
          action={can.manageCatalogue ? <PrimaryAdd onAdd={() => setOpened('new')} /> : undefined}
        />
      ) : (
        <div className="flex flex-col gap-10">
          {can.manageCatalogue ? null : <InlineBanner kind="info" message={c.readOnly} />}
          {rates.isError ? <InlineBanner kind="error" message={errorText(rates.error)} /> : null}
          {groupByCategory(active).map((group, index, groups) =>
            // One group with no category is just the list, as before categories.
            groups.length === 1 && group.category === null ? (
              <div key="all">{table(group.services, c.active)}</div>
            ) : (
              <section key={group.category ?? '—'} aria-labelledby={`category-${index}`} className="flex flex-col gap-2">
                <SectionHeader id={`category-${index}`} title={group.category ?? c.noCategory} count={group.services.length} />
                {table(group.services, group.category ?? c.noCategory)}
              </section>
            ),
          )}
          {retired.length > 0 ? (
            <section aria-labelledby="retired-services" className="flex flex-col gap-2">
              <SectionHeader id="retired-services" title={c.retired} count={retired.length} />
              {table(retired, c.retired)}
              <p className="px-4 text-caption text-pretty text-muted">{c.retiredFootnote}</p>
            </section>
          ) : null}
        </div>
      )}

      <ServicePanel
        ctx={ctx}
        service={open}
        rates={rates.data ?? []}
        categories={categorySuggestions(all)}
        onClose={() => setOpened(null)}
        // A new service stays open in its panel: providers and payout come next.
        onSaved={(saved) => setNews(c.savedBanner(saved.name))}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRetiring(null)
            retire.reset()
          }
        }}
        title={retiring ? c.retireTitle(retiring.name) : ''}
        message={c.retireMessage}
        confirmLabel={c.retireConfirm}
        busy={retire.isPending}
        error={retire.isError ? <InlineBanner kind="error" message={errorText(retire.error)} /> : null}
        onConfirm={() => {
          if (retiring)
            retire.mutate(retiring.id, {
              onSuccess: () => {
                setNews(c.retiredBanner(retiring.name))
                setRetiring(null)
              },
            })
        }}
      />
    </>
  )
}

/// The name cell: the service's picture as a small thumbnail (a quiet square
/// without one, so the names line up) and the name.
const ServiceName = ({ service }: { service: ClassType }) => {
  const src = photoSrc(service.imageUrl)
  return (
    <span className="flex min-w-0 items-center gap-3">
      <span aria-hidden="true" className="size-8 shrink-0 overflow-hidden rounded-[8px] bg-surface-3">
        {src ? <img src={src} alt="" className="size-full object-cover" /> : null}
      </span>
      <span className="min-w-0 truncate">{service.name}</span>
    </span>
  )
}
